import { useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AuthService } from "./services/auth";
import { useI18n } from "./i18n";

import Error from "./components/Error";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Spinner from "./components/Spinner";
import VideoList from "./components/videos/VideoList";
import VideoPlayerModal from "./components/videos/VideoPlayerModal";
import SharedClipPage from "./components/videos/SharedClipPage";
import PublicVideoPage from "./components/videos/PublicVideoPage";
import LogoutSuccess from "./components/LogoutSuccess";

function MainApp() {
  const { t } = useI18n();
  const auth = useAuth();
  const location = useLocation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  const [currentVideoKey, setCurrentVideoKey] = useState<string | null>(null);

  const handleWatchVideo = (videoUrl: string, videoKey: string) => {
    setCurrentVideoUrl(videoUrl);
    setCurrentVideoKey(videoKey);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentVideoUrl(null);
    setCurrentVideoKey(null);
  };

  useEffect(() => {
    if (
      !AuthService.isLoading(auth) &&
      !AuthService.isAuthenticated(auth) &&
      !AuthService.hasError(auth)
    ) {
      AuthService.signIn(auth);
    }
  }, [auth]);

  // Don't require authentication for logout success page
  if (location.pathname === "/logout-success") {
    return <LogoutSuccess />;
  }

  if (AuthService.isLoading(auth)) {
    return <Spinner text={t("app.signingIn")} />;
  }

  if (!AuthService.isAuthenticated(auth) && !AuthService.hasError(auth)) {
    return <Spinner text={t("app.redirecting")} />;
  }

  if (AuthService.hasError(auth)) {
    const error = AuthService.getError(auth);
    console.error("Authentication error:", error);
    return (
      <Error title={t("app.authErrorTitle")} message={t("app.authErrorBody")} />
    );
  }

  return (
    <div className="min-h-screen bg-base-200 flex flex-col">
      <Header />
      <main className="flex-1 px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto">
          <VideoList onWatchVideo={handleWatchVideo} auth={auth} />
        </div>
      </main>
      <Footer />
      <VideoPlayerModal
        isOpen={isModalOpen}
        videoUrl={currentVideoUrl}
        videoKey={currentVideoKey}
        onClose={handleCloseModal}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public, unauthenticated viewers -- must sit outside MainApp's auth gate */}
        <Route path="/s/:token" element={<SharedClipPage />} />
        <Route path="/p/:slug" element={<PublicVideoPage />} />
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </Router>
  );
}

export default App;
